import React from 'react';
import {
  Loader2,
  RefreshCw,
  Calendar,
  BarChart3,
  Building2,
  CreditCard,
  ArrowUpRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { useAdminInfrastructure } from '../../hooks/useAdminInfrastructure';
import useDashboardAmountVisibility from '../../hooks/useDashboardAmountVisibility';
import DashboardAmountVisibilityToggle from '../../components/shared/DashboardAmountVisibilityToggle';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import {
  KeyStatistics,
  RecentBookings,
  BookingStatusBreakdown,
  QuickActions,
  RevenueBreakdown,
  PlatformMetrics,
  RecentVendors,
  RecentPayments,
  RevenueTrendChart,
  BookingTrendChart,
  TopPerformers,
  SystemHealth,
  ActivityFeed,
  UserGrowthChart,
} from '../../components/admin/dashboard';

const PERIOD_TABS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'all', label: 'All' },
];

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-0.5">
    {children}
  </p>
);

const AdminDashboard = () => {
  const {
    analytics,
    isLoading: isLoadingAnalytics,
    isFetching: isFetchingAnalytics,
    period,
    setPeriod,
    refetch: refetchAnalytics,
  } = useAdminAnalytics('month');
  const {
    recentBookings,
    recentVendors,
    recentPayments,
    performance,
    isLoading: isLoadingDashboard,
    refetchAll: refetchDashboard,
  } = useAdminDashboard(period);
  const { infrastructure, refetch: refetchInfra } = useAdminInfrastructure();
  const { amountsVisible, toggleAmountsVisible } = useDashboardAmountVisibility('admin');

  const initialLoad = (isLoadingAnalytics || isLoadingDashboard) && !analytics?.overview;
  const refreshing = isFetchingAnalytics;
  const guide = ADMIN_PAGE_GUIDES.dashboard;

  const handleRefresh = () => {
    refetchAnalytics?.();
    refetchDashboard?.();
    refetchInfra?.();
  };

  return (
    <div className="space-y-6 max-w-[1600px]">
      <AdminPageHeader
        className="sm:items-center"
        title="Dashboard"
        subtitle="Platform pulse — bookings, revenue, and health. Marketplace figures exclude private bookings."
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div
              className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
              role="tablist"
              aria-label="Period"
            >
              {PERIOD_TABS.map((tab) => {
                const active = period === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setPeriod(tab.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      active
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <DashboardAmountVisibilityToggle
              amountsVisible={amountsVisible}
              onToggle={toggleAmountsVisible}
            />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium !rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      {guide ? <AdminGuideCallout title={guide.title} steps={guide.steps} /> : null}

      {/* Shortcut strip */}
      <div className="flex flex-wrap gap-2">
        {[
          { to: '/admin/bookings', label: 'Bookings', Icon: Calendar },
          { to: '/admin/analytics', label: 'Analytics', Icon: BarChart3 },
          { to: '/admin/vendors', label: 'Vendors', Icon: Building2 },
          { to: '/admin/payments', label: 'Payments', Icon: CreditCard },
          { to: '/admin/vendor-payouts', label: 'Payouts', Icon: ArrowUpRight },
        ].map(({ to, label, Icon }) => (
          <Link
            key={to}
            to={to}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium !rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Icon className="w-3.5 h-3.5 text-gray-500" />
            {label}
          </Link>
        ))}
      </div>

      {initialLoad ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-gray-200/80 bg-white py-24 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          Loading dashboard…
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <SectionLabel>Key metrics · {period}</SectionLabel>
              {refreshing ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Updating
                </span>
              ) : null}
            </div>
            <KeyStatistics analytics={analytics} amountsVisible={amountsVisible} />
          </section>

          <section className="space-y-3">
            <SectionLabel>Operations</SectionLabel>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <RecentBookings bookings={recentBookings} />
              <div className="space-y-5">
                <BookingStatusBreakdown bookingsAnalytics={analytics?.bookings_analytics} />
                <QuickActions />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <SectionLabel>Trends</SectionLabel>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <RevenueTrendChart trends={analytics?.trends?.revenue || []} />
              <BookingTrendChart trends={analytics?.trends?.bookings || []} />
            </div>
          </section>

          <section className="space-y-3">
            <SectionLabel>Revenue & performance</SectionLabel>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <RevenueBreakdown
                revenueAnalytics={analytics?.revenue_analytics}
                overview={analytics?.overview}
              />
              <PlatformMetrics bookingsAnalytics={analytics?.bookings_analytics} />
            </div>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <TopPerformers topPerformers={analytics?.top_performers || []} />
              <SystemHealth performance={performance} infrastructure={infrastructure} />
            </div>
          </section>

          <section className="space-y-3">
            <SectionLabel>Growth & activity</SectionLabel>
            <div className="grid min-h-[360px] grid-cols-1 gap-5 lg:grid-cols-2">
              <UserGrowthChart userAnalytics={analytics?.user_analytics} />
              <ActivityFeed />
            </div>
          </section>

          <section className="space-y-3">
            <SectionLabel>Marketplace activity</SectionLabel>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <RecentVendors vendors={recentVendors} />
              <RecentPayments payments={recentPayments} />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
